import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { AsyncSelect } from '@atlaskit/select';
import { useAutoCompleteOptions } from '@atlassian/eoc-auto-complete';

import { getNoOne } from '../../../utils/responder';

import { CustomInput } from './custom-input';
import { CustomOption } from './custom-option';
import { type AutoCompleteWithNoOneProps } from './types';

export const AutoCompleteWithNoOne = React.forwardRef(
	<IsMulti extends boolean = false>(
		props: AutoCompleteWithNoOneProps<IsMulti>,
		ref: React.Ref<any>,
	) => {
		const { id, ...rest } = props;

		const intlApi = useIntl();
		const getAutoCompleteOptions = useAutoCompleteOptions(props.endpoints);

		const [_, noOne] = useMemo(() => getNoOne(intlApi), [intlApi]);

		const getAutoCompleteOptionsWithNoOne = useCallback(
			async (value: string) => {
				const options = await getAutoCompleteOptions(value);

				return [...(options ?? []), noOne];
			},
			[getAutoCompleteOptions, noOne],
		);

		return (
			<AsyncSelect
				{...rest}
				ref={ref}
				inputId={id}
				menuPosition="absolute"
				loadOptions={getAutoCompleteOptionsWithNoOne}
				components={{
					Option: CustomOption,
					Input: CustomInput,
					...props.components,
				}}
			/>
		);
	},
) as <IsMulti extends boolean = false>(
	props: AutoCompleteWithNoOneProps<IsMulti> & { ref?: React.Ref<any> },
) => React.ReactElement;
